import type { IconName } from '@/components/common/Icon';
import { SCOPE_CODE } from '@/constants/scopes';

export const NOTIFICATION_CATEGORY = {
  visits: 'visits',
  messages: 'messages',
  forms: 'forms',
  records: 'records',
  announcements: 'announcements',
  support: 'support',
  workspace: 'workspace',
} as const;

export type NotificationCategory =
  (typeof NOTIFICATION_CATEGORY)[keyof typeof NOTIFICATION_CATEGORY];

export const NOTIFICATION_CATEGORY_LIST: NotificationCategory[] = [
  NOTIFICATION_CATEGORY.visits,
  NOTIFICATION_CATEGORY.messages,
  NOTIFICATION_CATEGORY.forms,
  NOTIFICATION_CATEGORY.records,
  NOTIFICATION_CATEGORY.announcements,
  NOTIFICATION_CATEGORY.support,
  NOTIFICATION_CATEGORY.workspace,
];

const PLATFORM_NOTIFICATION_CATEGORIES: NotificationCategory[] = [
  NOTIFICATION_CATEGORY.support,
  NOTIFICATION_CATEGORY.workspace,
];

const CLIENT_NOTIFICATION_CATEGORIES: NotificationCategory[] = [
  NOTIFICATION_CATEGORY.visits,
  NOTIFICATION_CATEGORY.messages,
  NOTIFICATION_CATEGORY.forms,
  NOTIFICATION_CATEGORY.records,
  NOTIFICATION_CATEGORY.announcements,
];

const WORKSPACE_STAFF_NOTIFICATION_CATEGORIES: NotificationCategory[] = [
  NOTIFICATION_CATEGORY.visits,
  NOTIFICATION_CATEGORY.messages,
  NOTIFICATION_CATEGORY.forms,
  NOTIFICATION_CATEGORY.records,
  NOTIFICATION_CATEGORY.support,
  NOTIFICATION_CATEGORY.workspace,
];

export function resolveNotificationCategoriesForScope(
  activeScopeCode: string | null
): NotificationCategory[] {
  if (activeScopeCode === SCOPE_CODE.platform) {
    return PLATFORM_NOTIFICATION_CATEGORIES;
  }
  if (activeScopeCode === SCOPE_CODE.client) {
    return CLIENT_NOTIFICATION_CATEGORIES;
  }
  return WORKSPACE_STAFF_NOTIFICATION_CATEGORIES;
}

const NOTIFICATION_CATEGORY_ICON: Record<NotificationCategory, IconName> = {
  visits: 'Calendar',
  messages: 'MessageCircle',
  forms: 'ClipboardCheck',
  records: 'FileText',
  announcements: 'Megaphone',
  support: 'Ticket',
  workspace: 'Building',
};

const DEFAULT_NOTIFICATION_CATEGORY: NotificationCategory = NOTIFICATION_CATEGORY.workspace;

const NOTIFICATION_TYPE_CATEGORY: Record<string, NotificationCategory> = {
  appointment_confirmation: NOTIFICATION_CATEGORY.visits,
  appointment_change: NOTIFICATION_CATEGORY.visits,
  appointment_cancellation: NOTIFICATION_CATEGORY.visits,
  appointment_reminder: NOTIFICATION_CATEGORY.visits,
  staff_appointment_cancelled_by_client: NOTIFICATION_CATEGORY.visits,
  staff_appointment_reschedule_requested: NOTIFICATION_CATEGORY.visits,
  chat_message_to_client: NOTIFICATION_CATEGORY.messages,
  chat_message_to_practitioner: NOTIFICATION_CATEGORY.messages,
  staff_intake_submitted: NOTIFICATION_CATEGORY.forms,
  client_intake_accepted: NOTIFICATION_CATEGORY.forms,
  client_intake_returned: NOTIFICATION_CATEGORY.forms,
  staff_consent_signed: NOTIFICATION_CATEGORY.forms,
  staff_consent_withdrawn: NOTIFICATION_CATEGORY.forms,
  client_consent_requested: NOTIFICATION_CATEGORY.forms,
  client_consent_update_required: NOTIFICATION_CATEGORY.forms,
  client_file_approved: NOTIFICATION_CATEGORY.records,
  client_file_rejected: NOTIFICATION_CATEGORY.records,
  staff_client_file_uploaded: NOTIFICATION_CATEGORY.records,
  client_treatment_plan_shared: NOTIFICATION_CATEGORY.records,
  client_treatment_plan_updated: NOTIFICATION_CATEGORY.records,
  client_session_summary_shared: NOTIFICATION_CATEGORY.records,
  client_first_consultation_shared: NOTIFICATION_CATEGORY.records,
  exercise_reminder: NOTIFICATION_CATEGORY.records,
  staff_pet_change_requested: NOTIFICATION_CATEGORY.records,
  client_pet_change_approved: NOTIFICATION_CATEGORY.records,
  client_pet_change_rejected: NOTIFICATION_CATEGORY.records,
  staff_client_details_updated: NOTIFICATION_CATEGORY.records,
  client_broadcast: NOTIFICATION_CATEGORY.announcements,
  client_marketing_broadcast: NOTIFICATION_CATEGORY.announcements,
  support_ticket_created: NOTIFICATION_CATEGORY.support,
  support_ticket_assigned: NOTIFICATION_CATEGORY.support,
  support_ticket_reply_to_practitioner: NOTIFICATION_CATEGORY.support,
  support_ticket_reply_to_platform: NOTIFICATION_CATEGORY.support,
  support_ticket_resolved: NOTIFICATION_CATEGORY.support,
  platform_workspace_registered: NOTIFICATION_CATEGORY.workspace,
  platform_member_joined: NOTIFICATION_CATEGORY.workspace,
  staff_custom_domain_live: NOTIFICATION_CATEGORY.workspace,
  staff_custom_domain_lost: NOTIFICATION_CATEGORY.workspace,
  staff_custom_domain_expiring: NOTIFICATION_CATEGORY.workspace,
  staff_workspace_closure_scheduled: NOTIFICATION_CATEGORY.workspace,
  staff_assistant_budget_warning: NOTIFICATION_CATEGORY.workspace,
};

export function resolveNotificationCategory(notificationType: string): NotificationCategory {
  const matchedCategory = NOTIFICATION_TYPE_CATEGORY[notificationType];
  if (matchedCategory === undefined) {
    return DEFAULT_NOTIFICATION_CATEGORY;
  }
  return matchedCategory;
}

export function resolveNotificationCategoryIcon(category: NotificationCategory): IconName {
  return NOTIFICATION_CATEGORY_ICON[category];
}

const CHAT_NOTIFICATION_TYPES: string[] = [
  'chat_message_to_client',
  'chat_message_to_practitioner',
];

export function isChatMessageNotificationType(notificationType: string): boolean {
  return CHAT_NOTIFICATION_TYPES.includes(notificationType);
}
